import {
    PropsWithChildren,
    useState,
    useRef,
    useEffect,
    useId,
    ReactNode
} from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ChevronRightLargeIcon from "@atlaskit/icon/core/chevron-right";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { Lozenge, LozengeAppearance } from "../../lozenge/src/lozenge";

const containerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "8px"
});

// CSS to hide the default summary marker
const buttonStyles = css({
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    gap: token("space.050")
});

export type ChevronColors = "color.primary.blue";

export interface ExpandProps extends PropsWithChildren {
    eventDataName?: string;
    actionSubject?: string;
    label: string | ReactNode;
    expandedLabelText?: string | ReactNode;
    boldLabel?: boolean;
    chevronColor?: ChevronColors;
    isExpanded?: boolean;
    lozengeText?: string;
    lozengeAppearance?: LozengeAppearance;
}

export const Expand = ({
    children,
    label,
    expandedLabelText,
    eventDataName = "expand",
    actionSubject = "expand",
    boldLabel,
    chevronColor,
    isExpanded = false,
    lozengeText,
    lozengeAppearance = "blue"
}: ExpandProps) => {
    const [disableTransition, setDisableTransition] = useState(true);

    const [expanded, setExpanded] = useState(isExpanded);
    const [height, setHeight] = useState(() => {
        // "auto" if expanded on mount to avoid transition
        return isExpanded ? "auto" : "0px";
    });
    const contentRef = useRef<HTMLDivElement>(null);
    const contentId = useId();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        setExpanded(isExpanded);
        if (contentRef.current && isExpanded) {
            setHeight(contentRef.current.scrollHeight + "px");
        } else if (!isExpanded && contentRef.current) {
            setHeight("0px");
        }
    }, [isExpanded]);

    const handleToggle = () => {
        if (disableTransition) {
            setDisableTransition(false);
        }

        if (!expanded && contentRef.current) {
            setHeight(contentRef.current.scrollHeight + "px");
            setExpanded(true);
        } else if (expanded && contentRef.current) {
            setHeight(contentRef.current.scrollHeight + "px"); // set to current height
            setTimeout(() => setHeight("0px"), 10); // next tick to trigger transition
            setExpanded(false);
        }

        if (eventDataName) {
            const expandedDirectionLabel = !expanded ? "open" : "close";
            createAnalyticsEvent({
                eventType: "ui",
                eventComponent: "button",
                action: "clicked",
                actionSubject: actionSubject ?? "expand",
                source: "expand-button",
                attributes: {
                    name: eventDataName,
                    label: expandedDirectionLabel,
                    direction: expandedDirectionLabel
                }
            }).fire();
        }
    };

    const handleTransitionEnd = () => {
        if (expanded && contentRef.current) {
            setHeight("auto");
        }
    };

    return (
        <div css={containerStyles}>
            <button
                data-testid="expand-button"
                css={buttonStyles}
                onClick={handleToggle}
                aria-expanded={expanded}
                aria-controls={contentId}
            >
                {/* custom expand icon */}
                <span
                    css={[
                        css({
                            height: "24px",
                            width: "24px",
                            transform: expanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                            transition: disableTransition
                                ? "none"
                                : "transform 0.1s ease-in-out"
                        }),
                        chevronColor && css({ color: wacToken(chevronColor) })
                    ]}
                >
                    <ChevronRightLargeIcon
                        label=""
                        size="small"
                        spacing="spacious"
                    />
                </span>

                <TypographyStyles>
                    <p
                        className="body-sm"
                        css={css({
                            margin: 0,
                            marginBlockStart: "1px",
                            textAlign: "left"
                        })}
                        style={{
                            fontWeight: boldLabel ? 700 : "inherit"
                        }}
                    >
                        {expanded && expandedLabelText
                            ? expandedLabelText
                            : label}{" "}
                        {lozengeText && (
                            <Lozenge
                                text={lozengeText}
                                appearance={lozengeAppearance}
                            />
                        )}
                    </p>
                </TypographyStyles>
            </button>
            <div
                data-testid="expand-content"
                id={contentId}
                ref={contentRef}
                css={css({
                    overflow: "hidden",
                    transition: `height ${
                        disableTransition ? "0s" : "0.15s ease-in-out"
                    }`,
                    height,
                    paddingInlineStart: `calc(${token("space.300")} + ${token(
                        "space.050"
                    )})`
                })}
                onTransitionEnd={handleTransitionEnd}
                aria-hidden={!expanded}
            >
                <TypographyStyles>
                    <div className="body-sm">{children}</div>
                </TypographyStyles>
            </div>
        </div>
    );
};
